// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { sharedTagStyle } from "@bg/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateOnlyStringToDate } from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DatePickerState } from "@insite/mobius/DatePicker";
import Tag, { horizontalStyles } from "@insite/mobius/Tag";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isFilterOpen: state.pages.savedOrderList.isFilterOpen,
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
    language: getSession(state).language,
});

const mapDispatchToProps = {
    updateSearchFields,
};

export interface SavedOrderListTagsStyles {
    appliedFiltersContainer?: InjectableCss;
}

export const savedOrderListFilterStyles: SavedOrderListTagsStyles = {
    appliedFiltersContainer: {
        css: css`
            ${horizontalStyles}
            && div[class*=""TagStyle-sc] {
                width: auto;
                height: auto !important;
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const styles = savedOrderListFilterStyles;
const savedOrderListFilterTags = ({ language, isFilterOpen, getCartsApiParameter, updateSearchFields }: Props) => {
    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: "",
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: "",
        });
    };

    const resetOrderSubtotal = () => {
        updateSearchFields({ orderSubtotalOperator: "", orderSubtotal: "" });
    };

    if (isFilterOpen) {
        return null;
    }

    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {getCartsApiParameter.fromDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        fromDateChangeHandler({} as any);
                    }}
                >
                    {`${translate("From")}: ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getCartsApiParameter.fromDate)!,
                        language,
                    })}`}{" "}
                </Tag>
            )}
            {getCartsApiParameter.toDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        toDateChangeHandler({} as any);
                    }}
                >
                    {`${translate("To")}: ${getLocalizedDateTime({
                        dateTime: convertDateOnlyStringToDate(getCartsApiParameter.toDate)!,
                        language,
                    })}`}
                </Tag>
            )}
            {getCartsApiParameter.orderSubtotalOperator && getCartsApiParameter.orderSubtotal && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        resetOrderSubtotal();
                    }}
                >
                    {`${translate("Order Total")}: ${getCartsApiParameter.orderSubtotalOperator} ${
                        getCartsApiParameter.orderSubtotal
                    }`}
                </Tag>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(savedOrderListFilterTags),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Tags",
        allowedContexts: ["SavedOrderListPage"],
    },
};

export default widgetModule;
